import { useState, useCallback, ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getBuildingDeviceTypes, HasUrlSlug, getBuildingIdByUrlSlug } from 'selectors/buildingsSelectors';
import { values, Dictionary } from 'lodash';
import { useParams } from 'util/reactRouterHooks';
import { State } from 'reducers';

export type Props = {
    selectedDeviceTypes: Dictionary<boolean>;
    onChange(fn: (deviceTypes: Dictionary<boolean>) => Dictionary<boolean>): void;
};

export const DeviceTypeFilter = ({ selectedDeviceTypes, onChange }: Props) => {
    const { t } = useTranslation();

    const params = useParams<HasUrlSlug>();
    const buildingId = useSelector((state: State) => getBuildingIdByUrlSlug(state, params));
    const deviceTypesMap = useSelector((state: State) => buildingId ? getBuildingDeviceTypes(state, { buildingId }) : {});

    const deviceTypes = useMemo(() => {
        return values(deviceTypesMap)
            .sort((a, b) =>
                a.displayName.localeCompare(b.displayName)
            );
    }, [ deviceTypesMap ]);

    const [ collapsed, setCollapsed ] = useState(false);

    const handleClickCheckbox = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { id } = e.currentTarget.dataset;
        onChange(dt => ({
            ...dt,
            [id!]: !dt[id!],
        }));
    }, [ onChange ]);
    
    const handleClickHeader = useCallback(() => {
        setCollapsed(c => !c);
    }, [ setCollapsed ]);

    return (
        <div
            id="device-type-filter"
            className={ `device-filter ${collapsed ? 'collapsed' : ''}` }
        >
            <header
                onClick={ handleClickHeader }
            >
                <i className="ui down triangle icon"/>
                <span>
                    { t('DEVICES.FILTERS.DEVICE_TYPE') }
                </span>
            </header>
            { !collapsed &&
                <ul>
                    { deviceTypes.length === 0
                        ? <li className="disabled">
                            { t('DEVICES.FILTERS.NO_DEVICES') }
                        </li>
                        : deviceTypes.map(dt =>
                            <li
                                key={ dt.id }
                            >
                                <label>
                                    <input
                                        type="checkbox"
                                        data-id={ dt.id }
                                        checked={ !!selectedDeviceTypes[dt.id] }
                                        onChange={ handleClickCheckbox }
                                    />
                                    <span>
                                        { dt.displayName }
                                    </span>
                                </label>
                            </li>
                        )
                    }
                </ul>
            }
        </div>
    );
};
